import { IconAlertCircle } from '@tabler/icons-react';
import { ReactElement, useMemo, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOnCallSchedule } from './hooks/useOnCallSchedule';
import { OnCallScheduleRow } from './components/OnCallScheduleRow';
import { ON_CALL_NP_ELIGIBLE_ROLES } from '@bariendo/lib/constants';
import { OnCallMonthlyStats } from './components/OnCallMonthlyStats';
import { getOnCallScheduleWindow } from '@bariendo/lib/on-call';
import { usePractitionersByRoles } from '@/hooks/usePractitionersByRoles';
import { OnCallBulkEditToolbar } from './components/OnCallBulkEditToolbar';
import { Alert, Center, Checkbox, Container, Loader, Table, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { getBrowserTimezone, timezoneToFriendlyLabel } from '@/pages/SchedulingPage/utils/common';

/**
 * Who is on call is something the whole company needs to be able to look up, so the page
 * is not gated — CompartmentAccessPolicy already grants every provider readonly Schedule
 * and Slot. Only the edit controls are restricted, to the roles holding
 * OnCallScheduleAccessPolicy (or AdminAccessPolicy), so what the UI offers matches what the
 * server will accept rather than presenting controls that 403 on save.
 */
export function OnCallSchedulePage(): ReactElement {
  const {
    permissionsLoading,
    currentUserPermissions: { canEditOnCallSchedule },
  } = useCurrentUser();

  // canEditOnCallSchedule starts false and resolves asynchronously, and it decides both what
  // the page renders and whether loading may create a missing Schedule. Rendering before it
  // settles gives an editor the read-only view, then a second full fetch when it flips.
  if (permissionsLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return <OnCallScheduleView canEdit={canEditOnCallSchedule} />;
}

function OnCallScheduleView({ canEdit }: { canEdit: boolean }): ReactElement {
  const { loading, error, rows, rowSaveStates, schedule, revision, saveRow, applyBulk } = useOnCallSchedule({
    canEdit,
  });
  const { practitionerOptions, loading: practitionersLoading } = usePractitionersByRoles(ON_CALL_NP_ELIGIBLE_ROLES);
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());

  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const horizonLabel = useMemo(
    () => dayjs(getOnCallScheduleWindow(browserTimezone).rangeEnd).format('MMMM YYYY'),
    [browserTimezone]
  );

  const rowsByDate = useMemo(() => new Map(rows.map((row) => [row.date, row])), [rows]);
  const allSelected = rows.length > 0 && selectedDates.size === rows.length;
  const someSelected = selectedDates.size > 0 && !allSelected;

  const toggleAll = (checked: boolean) => {
    setSelectedDates(checked ? new Set(rows.map((row) => row.date)) : new Set());
  };

  const toggleRow = (date: string, checked: boolean) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(date);
      } else {
        next.delete(date);
      }
      return next;
    });
  };

  const handleBulkApply = async (primaryId: string | null, backupId: string | null) => {
    const dates = [...selectedDates];
    await applyBulk(dates, primaryId, backupId, rowsByDate);
    setSelectedDates(new Set());
  };

  return (
    <Container size="xl" py="md">
      <Title order={1} mb="lg">
        On-call schedule
      </Title>

      <OnCallMonthlyStats schedule={schedule} timezone={browserTimezone} revision={revision} />

      <Text c="dimmed" mb="md">
        Coverage through the end of {horizonLabel}. All times are shown in your local time (
        {timezoneToFriendlyLabel(browserTimezone)}).{canEdit ? ' Changes save as you edit each row.' : ''}
      </Text>

      {error ? (
        <Alert icon={<IconAlertCircle size={16} />} title="Failed to load schedule" color="red" mb="md">
          {error}
        </Alert>
      ) : null}

      {canEdit && (
        <OnCallBulkEditToolbar
          selectedCount={selectedDates.size}
          practitionerOptions={practitionerOptions}
          practitionersLoading={practitionersLoading}
          disabled={loading}
          onApply={handleBulkApply}
        />
      )}

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Table striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {canEdit && (
                <Table.Th w={48}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(event) => toggleAll(event.currentTarget.checked)}
                  />
                </Table.Th>
              )}
              <Table.Th>Day</Table.Th>
              <Table.Th>Primary on-call practitioner</Table.Th>
              <Table.Th>Backup practitioner</Table.Th>
              <Table.Th>Start time</Table.Th>
              <Table.Th>End time</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <OnCallScheduleRow
                key={row.date}
                row={row}
                selected={selectedDates.has(row.date)}
                practitionerOptions={practitionerOptions}
                practitionersLoading={practitionersLoading}
                saveState={rowSaveStates[row.date]}
                disabled={!canEdit}
                selectable={canEdit}
                onSelectChange={(selected) => toggleRow(row.date, selected)}
                onSave={saveRow}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Container>
  );
}
